import React, { useState, useEffect } from 'react';
import {
    MdAdd, MdEdit, MdDelete, MdClose, MdPeople, MdAdminPanelSettings,
    MdPerson, MdEmail, MdCheckCircle, MdBusiness
} from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { useLanguage, translateUiText } from '../i18n';
import JoinCodeCard from '../components/JoinCodeCard';

const JOB_TITLES = [
    'technician', 'cleaner', 'electrician', 'plumber',
    'security', 'receptionist', 'groundskeeper', 'other'
];

function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><MdClose size={20} /></button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

const roleColor = { admin: '#7c3aed', manager: '#0369a1', employee: '#0d9488' };
const roleBg = { admin: '#f3e8ff', manager: '#e0f2fe', employee: '#ccfbf1' };
const roleIcon = { admin: <MdAdminPanelSettings />, manager: <MdPeople />, employee: <MdPerson /> };

function jobLabel(jt) {
    return jt ? jt.charAt(0).toUpperCase() + jt.slice(1) : '—';
}

export default function Users() {
    const { user, isAdmin, isManager, isDeveloper, assignedFacility } = useAuth();
    const { t } = useLanguage();
    const [users, setUsers] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [createResult, setCreateResult] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [createForm, setCreateForm] = useState({
        name: '', email: '', role: 'employee', job_title: 'technician',
        department: '', phone: '', facility_id: ''
    });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const { data } = await API.get('/auth/users'); setUsers(data); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (isAdmin) {
            API.get('/spaces/buildings').then(r => setFacilities(r.data));
        }
    }, [isAdmin]);

    const openEdit = u => {
        setSelectedUser(u);
        setEditForm({
            name: u.name, email: u.email || '', phone: u.phone || '', department: u.department || '',
            job_title: u.job_title || 'technician', role: u.role,
            facility_id: u.facility_id || '', is_active: u.is_active
        });
        setShowEditModal(true);
    };

    const handleUpdate = async e => {
        e.preventDefault(); setSaving(true);
        try {
            await API.put(`/auth/users/${selectedUser.id}`, editForm);
            setShowEditModal(false); load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Update failed')); }
        finally { setSaving(false); }
    };

    const handleCreate = async e => {
        e.preventDefault(); setSaving(true);
        try {
            // Manager: force role=employee, facility from their own
            const payload = { ...createForm };
            if (isManager) {
                payload.role = 'employee';
                payload.facility_id = assignedFacility?.id || '';
            }
            const { data } = await API.post('/auth/register', payload);
            setShowCreateModal(false);
            setCreateForm({ name: '', email: '', role: 'employee', job_title: 'technician', department: '', phone: '', facility_id: '' });
            setCreateResult({ ...data, email: createForm.email, name: createForm.name });
            setShowResultModal(true);
            load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Create failed')); }
        finally { setSaving(false); }
    };

    const handleDelete = async id => {
        if (!window.confirm(translateUiText('Permanently delete this user? This cannot be undone.'))) return;
        try {
            await API.delete(`/auth/users/${id}`);
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Permanent deletion failed'));
        }
    };

    const roleCounts = users.reduce((acc, u) => {
        acc[u.role] = (acc[u.role] || 0) + 1; return acc;
    }, {});

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>
                        {isManager ? t('users.pageTitleManager', '', { facility: assignedFacility?.name || t('users.myFacilityFallback') }) : t('users.pageTitleAdmin')}
                    </h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {isManager
                            ? t('users.subtitleManager')
                            : t('users.subtitleAdmin')}
                    </p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdAdd size={18} /> {isManager ? t('users.addEmployee') : t('users.addUser')}
                </button>
            </div>

            {isAdmin && <JoinCodeCard />}

            {/* Stats */}
            <div className="row g-3 mb-4">
                {Object.keys(roleColor).map(role => (
                    <div key={role} className="col-md-4 col-6">
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: roleBg[role], color: roleColor[role] }}>
                                {roleIcon[role]}
                            </div>
                            <div>
                                <div className="stat-value">{roleCounts[role] || 0}</div>
                                <div className="stat-label" style={{ textTransform: 'capitalize' }}>{role}s</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="page-card">
                <div style={{ overflowX: 'auto' }}>
                    {loading ? <div className="spinner-wrap"><div className="spinner-border" /></div> : (
                        <table className="fms-table">
                            <thead>
                                <tr>
                                    <th>{t('users.colName')}</th><th>{t('users.colEmail')}</th><th>{t('users.colRole')}</th><th>{t('users.colJobTitle')}</th>
                                    <th>{t('users.colFacility')}</th><th>{t('users.colDepartment')}</th><th>{t('users.colStatus')}</th><th>{t('users.colJoined')}</th><th>{t('users.colActions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{
                                                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                                    background: `linear-gradient(135deg,${roleColor[u.role] || '#0ea5e9'},#0284c7)`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#fff', fontSize: 13, fontWeight: 700
                                                }}>{u.name.charAt(0).toUpperCase()}</div>
                                                <span style={{ fontWeight: 500 }}>{u.name}</span>
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 13, color: '#64748b' }}>{u.email}</td>
                                        <td>
                                            <span style={{
                                                fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 12,
                                                background: roleBg[u.role] || '#f1f5f9', color: roleColor[u.role] || '#475569',
                                                textTransform: 'capitalize', display: 'inline-flex', alignItems: 'center', gap: 4
                                            }}>
                                                {roleIcon[u.role]} {u.role}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 13 }}>
                                            {u.role === 'employee'
                                                ? <span style={{ fontSize: 12, background: '#f1f5f9', padding: '2px 8px', borderRadius: 8 }}>
                                                    {jobLabel(u.job_title)}
                                                </span>
                                                : <span style={{ color: '#94a3b8' }}>—</span>
                                            }
                                        </td>
                                        <td style={{ fontSize: 12, color: '#64748b' }}>
                                            {facilities.find(f => f.id === u.facility_id)?.name || (u.facility_id ? `#${u.facility_id}` : '—')}
                                        </td>
                                        <td style={{ fontSize: 13 }}>{u.department || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                        <td>
                                            <span className={`badge-status ${u.is_active ? 'active' : 'inactive'}`}>
                                                {u.is_active ? t('users.active') : t('users.inactive')}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 12, color: '#94a3b8' }}>
                                            {new Date(u.created_at).toLocaleDateString()}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button className="btn btn-sm btn-light" onClick={() => openEdit(u)}>
                                                    <MdEdit size={15} />
                                                </button>
                                                {(isAdmin || isDeveloper) && String(u.id) !== String(user.id) && (
                                                    <button className="btn btn-sm btn-light" style={{ color: '#ef4444' }} onClick={() => handleDelete(u.id)}>
                                                        <MdDelete size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!users.length && (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                                        {t('users.noneFound')}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <Modal title={t('users.editModalTitle', '', { name: selectedUser?.name })} onClose={() => setShowEditModal(false)}>
                    <form onSubmit={handleUpdate}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('users.fullNameLabel')}</label>
                                <input className="form-control" value={editForm.name}
                                    onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                            </div>
                            {(isAdmin || isDeveloper) && <div className="col-12">
                                <label className="form-label fw-semibold">{t('users.emailLabel')}</label>
                                <input type="email" className="form-control" value={editForm.email}
                                    onChange={e => setEditForm({ ...editForm, email: e.target.value })} required />
                            </div>}
                            {isAdmin && (
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('users.roleLabel')}</label>
                                    <select className="form-select" value={editForm.role}
                                        onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
                                        <option value="admin">{t('role.admin')}</option>
                                        <option value="manager">{t('role.manager')}</option>
                                        <option value="employee">{t('role.employee')}</option>
                                    </select>
                                </div>
                            )}
                            {(editForm.role === 'employee' || isManager) && (
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('users.jobTitleLabel')}</label>
                                    <select className="form-select" value={editForm.job_title}
                                        onChange={e => setEditForm({ ...editForm, job_title: e.target.value })}>
                                        {JOB_TITLES.map(t => <option key={t} value={t}>{jobLabel(t)}</option>)}
                                    </select>
                                </div>
                            )}
                            {isAdmin && (
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('users.facilityLabel')}</label>
                                    <select className="form-select" value={editForm.facility_id}
                                        onChange={e => setEditForm({ ...editForm, facility_id: e.target.value })}>
                                        <option value="">{t('users.noFacility')}</option>
                                        {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                    </select>
                                </div>
                            )}
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('users.departmentLabel')}</label>
                                <input className="form-control" value={editForm.department}
                                    onChange={e => setEditForm({ ...editForm, department: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('users.phoneLabel')}</label>
                                <input className="form-control" value={editForm.phone}
                                    onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
                            </div>
                            {isAdmin && (
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('users.statusLabel')}</label>
                                    <select className="form-select"
                                        value={editForm.is_active ? '1' : '0'}
                                        onChange={e => setEditForm({ ...editForm, is_active: e.target.value === '1' })}>
                                        <option value="1">{t('users.active')}</option>
                                        <option value="0">{t('users.inactive')}</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowEditModal(false)}>{t('users.cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('users.saving')}</> : t('users.saveChanges')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <Modal title={isManager ? t('users.createModalTitleManager') : t('users.createModalTitleAdmin')}
                    onClose={() => setShowCreateModal(false)}>
                    <div style={{ background: '#e0f2fe', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#0369a1', marginBottom: 16 }}>
                        <MdEmail style={{ marginRight: 6 }} />
                        {t('users.tempPasswordNote')}
                    </div>
                    {isManager && assignedFacility && (
                        <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#15803d', marginBottom: 16 }}>
                            <MdBusiness style={{ marginRight: 6 }} />
                            {t('users.willBeAddedTo', '', { facility: assignedFacility.name })}
                        </div>
                    )}
                    <form onSubmit={handleCreate}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('users.fullNameLabel')} *</label>
                                <input className="form-control" value={createForm.name}
                                    onChange={e => setCreateForm({ ...createForm, name: e.target.value })} required />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('users.emailLabel')} *</label>
                                <input type="email" className="form-control" value={createForm.email}
                                    onChange={e => setCreateForm({ ...createForm, email: e.target.value })} required />
                            </div>
                            {isAdmin && (
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('users.roleLabel')}</label>
                                    <select className="form-select" value={createForm.role}
                                        onChange={e => setCreateForm({ ...createForm, role: e.target.value })}>
                                        <option value="admin">{t('role.admin')}</option>
                                        <option value="manager">{t('role.manager')}</option>
                                        <option value="employee">{t('role.employee')}</option>
                                    </select>
                                </div>
                            )}
                            {(createForm.role === 'employee' || isManager) && (
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('users.jobTitleLabel')}</label>
                                    <select className="form-select" value={createForm.job_title}
                                        onChange={e => setCreateForm({ ...createForm, job_title: e.target.value })}>
                                        {JOB_TITLES.map(t => <option key={t} value={t}>{jobLabel(t)}</option>)}
                                    </select>
                                </div>
                            )}
                            {isAdmin && createForm.role !== 'admin' && (
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('users.facilityLabel')}</label>
                                    <select className="form-select" value={createForm.facility_id}
                                        onChange={e => setCreateForm({ ...createForm, facility_id: e.target.value })}>
                                        <option value="">{t('users.noFacility')}</option>
                                        {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                    </select>
                                </div>
                            )}
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('users.departmentLabel')}</label>
                                <input className="form-control" value={createForm.department}
                                    onChange={e => setCreateForm({ ...createForm, department: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('users.phoneLabel')}</label>
                                <input className="form-control" value={createForm.phone}
                                    onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowCreateModal(false)}>{t('users.cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('users.creating')}</> : t('users.createUser')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Result Modal */}
            {showResultModal && createResult && (
                <Modal title={t('users.createdModalTitle')} onClose={() => { setShowResultModal(false); setCreateResult(null); }}>
                    <div style={{ textAlign: 'center', marginBottom: 20 }}>
                        <MdCheckCircle size={48} style={{ color: '#22c55e', marginBottom: 8 }} />
                        <h6 style={{ fontWeight: 700 }}>{t('users.accountCreatedFor', '', { name: createResult.name })}</h6>
                    </div>
                    {createResult.email_sent ? (
                        <div style={{ background: '#dcfce7', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#15803d', marginBottom: 16 }}>
                            {t('users.welcomeEmailSent', '', { email: createResult.email })}
                        </div>
                    ) : (
                        <div style={{ background: '#fef3c7', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#b45309', marginBottom: 16 }}>
                            {t('users.welcomeEmailFailed', '', { msg: createResult.email_message })}
                        </div>
                    )}
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                        {t('users.canChangePasswordNote')}
                    </div>
                    <button className="btn btn-primary w-100"
                        onClick={() => { setShowResultModal(false); setCreateResult(null); }}>
                        {t('users.done')}
                    </button>
                </Modal>
            )}
        </div>
    );
}
